/**
 * SP7 §2 — the shared snapshot-finalization core. Composes eval-context
 * assembly → applyBalancePolicy → transaction rules into ONE pure function
 * with two homes: the server recompute (rule-runtime/transaction-regroup.ts)
 * and the station live balance (apps/web live-balance.ts) — the SP2 balancer
 * pattern, so what the operator previews and what accept decides can never
 * drift. No I/O, no Date.now (computedAt flows from the caller's base).
 */
import { applyBalancePolicy, materializeTransactionFields, } from "@capture/shared";
import { buildTransactionEvalContext } from "./transaction-context.js";
import { evaluateExpression } from "./parser/expression.js";
/** true/nonzero-number ⇒ true; false/0 ⇒ false; null/anything else ⇒ null
 *  (SP5 Task 6 binding contract item 3 — moved verbatim from transaction-regroup). */
function toBool(v) {
    if (typeof v === "boolean")
        return v;
    if (typeof v === "number")
        return v !== 0;
    return null;
}
/** `field.<name>` prefixed entries for a TXN_* row (moved verbatim). */
function fieldRowEntries(fields) {
    const out = {};
    for (const [k, v] of Object.entries(fields))
        out[`field.${k}`] = v;
    return out;
}
export function deriveTransactionSnapshot(input) {
    const warn = input.onWarn ?? (() => { });
    const promotedFields = materializeTransactionFields(input.config.transactionFields ?? undefined, input.docsInOrder.map((d) => ({ classificationKey: d.classificationKey, fields: d.fields })));
    const paymentDocs = input.docsInOrder.filter((d) => d.role === "payment");
    const couponDocs = input.docsInOrder.filter((d) => d.role === "coupon");
    const checks = paymentDocs.map((d) => ({ documentId: d.documentId, amountCents: d.amountCents, ...fieldRowEntries(d.fields) }));
    const coupons = couponDocs.map((d) => ({ documentId: d.documentId, appliedAmountCents: d.amountCents, ...fieldRowEntries(d.fields) }));
    const ctx = buildTransactionEvalContext({
        transaction: {
            ordinal: input.transaction.ordinal,
            state: input.transaction.state,
            verdict: input.base.verdict,
            paymentTotalCents: input.base.paymentTotalCents,
            appliedTotalCents: input.base.appliedTotalCents,
            deltaCents: input.base.deltaCents,
        },
        promotedFields,
        checks,
        coupons,
        allocations: input.allocations.map((r) => ({ ...r })),
    });
    const evalWhen = (expr) => {
        try {
            return toBool(evaluateExpression(expr, ctx));
        }
        catch (e) {
            warn(`[deriveTransactionSnapshot] policy when-expression failed: ${e.message}`);
            return null;
        }
    };
    const couponsForPolicy = couponDocs.map((d) => ({ documentId: d.documentId, appliedCents: d.amountCents }));
    const outcome = applyBalancePolicy({
        base: input.base,
        policy: input.config.balancePolicy ?? undefined,
        allocations: input.allocations,
        coupons: couponsForPolicy,
        evalWhen,
    });
    let snapshot = outcome.snapshot;
    for (const rule of input.config.transactionRules ?? []) {
        let raw;
        try {
            raw = evaluateExpression(rule.when, ctx);
        }
        catch (e) {
            warn(`[deriveTransactionSnapshot] transaction rule '${rule.id}' failed: ${e.message}`);
            continue;
        }
        const result = toBool(raw);
        if (result === null) {
            warn(`[deriveTransactionSnapshot] transaction rule '${rule.id}' when-expression returned null/unevaluable — skipped`);
            continue;
        }
        if (result) {
            snapshot = {
                ...snapshot,
                ruleFlags: [...(snapshot.ruleFlags ?? []), { ruleId: rule.id, severity: rule.severity, message: rule.message }],
            };
        }
    }
    const nonPolicyRows = input.allocations.filter((r) => r.source !== "policy");
    return { snapshot, outcome, finalAllocations: [...nonPolicyRows, ...outcome.policyRows], promotedFields };
}
//# sourceMappingURL=transaction-snapshot.js.map